import { API_BASE } from '@/shared/services/api';
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Camera, Check, X, RotateCcw, DoorOpen, Hand, Lock,
  ChevronLeft, AlertCircle
} from 'lucide-react';
import { MobileButton, MobileCard } from '../components/MobileUIKit';
import '../styles/mobile.css';

const API_URL = API_BASE;

// Icon mapping for dynamically loaded icons
const ICON_MAP = {
  DoorOpen, Hand, Lock
};

export const DriverPODCapture = () => {
  const navigate = useNavigate();
  const [deliveryMethods, setDeliveryMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState('');
  const [photo, setPhoto] = useState(null);
  const [signature, setSignature] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  // Fetch delivery methods from API
  useEffect(() => {
    const fetchDeliveryMethods = async () => {
      try {
        const response = await fetch(`${API_URL}/api/reference/pod-methods`);
        if (response.ok) {
          const data = await response.json();
          // Map to local format with icons
          const methods = data.slice(0, 3).map(m => ({
            id: m.code.toLowerCase(),
            icon: ICON_MAP[m.icon] || DoorOpen,
            label: m.label
          }));
          setDeliveryMethods(methods);
          if (methods.length > 0) {
            setSelectedMethod(methods[0].id);
          }
        }
      } catch (error) {
        console.error('Failed to fetch POD methods:', error);
        // Fallback
        setDeliveryMethods([
          { id: 'door', icon: DoorOpen, label: 'Leave at Door' },
          { id: 'hand', icon: Hand, label: 'Hand to Customer' },
          { id: 'locker', icon: Lock, label: 'Locker/Safe' },
        ]);
        setSelectedMethod('door');
      }
    };
    fetchDeliveryMethods();
  }, []);

  // Mock photo capture
  const handleTakePhoto = () => {
    setIsCapturing(true);
    setTimeout(() => {
      setPhoto('captured');
      setIsCapturing(false);
    }, 1000);
  };

  const handleRetakePhoto = () => {
    setPhoto(null);
  };

  // Signature pad handlers
  const startDrawing = (e) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.strokeStyle = 'var(--mobile-text-primary)';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
    setSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignature(null);
  };

  const handleSubmit = async () => {
    if (!photo) {
      alert('Please take a photo first');
      return;
    }

    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Navigate to success screen
    navigate('/driver/success');
  };

  return (
    <div className="mobile-app min-h-screen mobile-safe-top">
      {/* Header */}
      <div className="mobile-px-4 pt-4 pb-3 mobile-flex mobile-items-center mobile-gap-3">
        <button 
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: 'var(--mobile-bg-tertiary)' }}
        >
          <ChevronLeft className="w-5 h-5" style={{ color: 'var(--mobile-text-primary)' }} />
        </button>
        <h1 className="mobile-text-title" style={{ color: 'var(--mobile-text-primary)' }}>
          Delivery Confirmation
        </h1>
      </div>

      <div className="mobile-px-4 pb-8">
        {/* Delivery Info */}
        <MobileCard className="mb-4">
          <p className="mobile-text-tiny" style={{ color: 'var(--mobile-text-tertiary)' }}>
            DELIVERING TO
          </p>
          <p 
            className="font-semibold mt-1"
            style={{ color: 'var(--mobile-text-primary)' }}
          >
            456 Oak Avenue, Apt 3B
          </p>
          <p className="mobile-text-caption">John Smith • Order #ORD-2026-4521</p>
        </MobileCard>

        {/* Delivery Method */}
        <div className="mb-6">
          <p 
            className="mobile-text-caption font-medium mb-3"
            style={{ color: 'var(--mobile-text-secondary)' }}
          >
            DELIVERY METHOD
          </p>
          <div className="mobile-flex mobile-gap-2">
            {deliveryMethods.map(method => {
              const Icon = method.icon;
              const isSelected = selectedMethod === method.id;
              return (
                <button
                  key={method.id}
                  onClick={() => setSelectedMethod(method.id)}
                  className="flex-1 flex flex-col items-center py-4 rounded-xl transition-all"
                  style={{
                    background: isSelected ? 'var(--ezp-primary)' : 'var(--mobile-bg-secondary)',
                    border: `2px solid ${isSelected ? 'var(--ezp-primary)' : 'var(--mobile-border)'}`,
                  }}
                >
                  <Icon 
                    className="w-6 h-6 mb-2" 
                    style={{ color: isSelected ? 'white' : 'var(--mobile-text-secondary)' }}
                  />
                  <span 
                    className="text-xs font-medium"
                    style={{ color: isSelected ? 'white' : 'var(--mobile-text-secondary)' }}
                  >
                    {method.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Photo Capture */}
        <div className="mb-6">
          <p 
            className="mobile-text-caption font-medium mb-3 mobile-flex mobile-items-center mobile-gap-2"
            style={{ color: 'var(--mobile-text-secondary)' }}
          >
            <Camera className="w-4 h-4" />
            PHOTO PROOF (Required)
          </p>
          
          {!photo ? (
            <div 
              className="relative aspect-[4/3] rounded-xl overflow-hidden"
              style={{ background: 'var(--mobile-bg-tertiary)' }}
            >
              {isCapturing ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full mobile-animate-spin" />
                </div>
              ) : (
                <button
                  onClick={handleTakePhoto}
                  className="absolute inset-0 flex flex-col items-center justify-center"
                >
                  <div 
                    className="w-16 h-16 rounded-full flex items-center justify-center mb-3"
                    style={{ background: 'var(--ezp-primary)' }}
                  >
                    <Camera className="w-8 h-8 text-white" />
                  </div>
                  <p style={{ color: 'var(--mobile-text-secondary)' }}>Tap to take photo</p>
                </button>
              )}
              
              {/* Camera overlay guides */}
              <div 
                className="absolute inset-4 border-2 border-dashed rounded-lg pointer-events-none"
                style={{ borderColor: 'var(--mobile-text-disabled)' }}
              />
            </div>
          ) : (
            <div className="relative aspect-[4/3] rounded-xl overflow-hidden">
              {/* Placeholder for captured photo */}
              <div 
                className="absolute inset-0 flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #2d3436 0%, #636e72 100%)' }}
              >
                <div className="text-center">
                  <Check 
                    className="w-16 h-16 mx-auto mb-2" 
                    style={{ color: 'var(--mobile-success)' }}
                  />
                  <p style={{ color: 'var(--mobile-text-primary)' }}>Photo captured</p>
                </div>
              </div>
              
              {/* Retake button */}
              <button
                onClick={handleRetakePhoto}
                className="absolute top-3 right-3 w-10 h-10 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(0,0,0,0.5)' }}
              >
                <RotateCcw className="w-5 h-5 text-white" />
              </button>
            </div>
          )}
        </div>

        {/* Signature Pad */}
        {selectedMethod === 'hand' && (
          <div className="mb-6 mobile-animate-fade-in">
            <div className="mobile-flex mobile-justify-between mobile-items-center mb-3">
              <p 
                className="mobile-text-caption font-medium mobile-flex mobile-items-center mobile-gap-2"
                style={{ color: 'var(--mobile-text-secondary)' }}
              >
                ✍️ CUSTOMER SIGNATURE
              </p>
              {signature && (
                <button
                  onClick={clearSignature}
                  className="text-xs font-medium"
                  style={{ color: 'var(--ezp-primary)' }}
                >
                  Clear
                </button>
              )}
            </div>
            
            <div 
              className="rounded-xl overflow-hidden"
              style={{ 
                background: 'var(--mobile-bg-secondary)',
                border: '2px dashed var(--mobile-border)'
              }}
            >
              <canvas
                ref={canvasRef}
                width={350}
                height={150}
                className="w-full touch-none"
                style={{ 
                  background: 'var(--mobile-bg-secondary)',
                }}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
              {!signature && (
                <p 
                  className="text-center py-4 mobile-text-caption"
                  style={{ color: 'var(--mobile-text-disabled)' }}
                >
                  Sign here
                </p>
              )}
            </div>
          </div>
        )}

        {/* Notes (optional) */}
        <div className="mb-6">
          <p 
            className="mobile-text-caption font-medium mb-3"
            style={{ color: 'var(--mobile-text-secondary)' }}
          >
            DELIVERY NOTES (Optional)
          </p>
          <textarea
            placeholder="Add any notes about the delivery..."
            className="w-full p-3 rounded-xl resize-none"
            rows={3}
            style={{
              background: 'var(--mobile-bg-secondary)',
              border: '1px solid var(--mobile-border)',
              color: 'var(--mobile-text-primary)',
            }}
          />
        </div>

        {/* Warning */}
        {!photo && (
          <div 
            className="mobile-flex mobile-items-center mobile-gap-2 p-3 rounded-lg mb-4"
            style={{ background: 'var(--mobile-warning-muted)' }}
          >
            <AlertCircle className="w-4 h-4" style={{ color: 'var(--mobile-warning)' }} />
            <p className="mobile-text-tiny" style={{ color: 'var(--mobile-warning)' }}>
              Photo is required to confirm delivery
            </p>
          </div>
        )}

        {/* Submit Button */}
        <MobileButton
          variant="success"
          fullWidth
          size="large"
          icon={Check}
          loading={isSubmitting}
          disabled={!photo}
          onClick={handleSubmit}
        >
          Confirm Delivery
        </MobileButton>
      </div>
    </div>
  );
};

export default DriverPODCapture;
